/**
 * /products — the shop's catalog (Stripe's Products and Prices).
 *
 * Every price a buyer pays comes from here: payment links and the buy button
 * only name a product or price. A price never changes; editing it adds a new
 * price and archives the old one, so links and carts that already point at
 * the product keep working at the new price.
 */

import { useEffect, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { ApiError } from "../lib/api";
import {
  createPaymentLink,
  createProduct,
  listProducts,
  priceLabel,
  toCents,
  updateProduct,
  uploadProductImage,
  type PriceData,
  type Product,
} from "../lib/catalog";
import { Banner, Btn } from "../components/ui";
import { Ic } from "../components/Icons";

export const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: "11px 13px",
  fontSize: 14,
  border: "1px solid var(--line, #e6e8eb)",
  borderRadius: 10,
  background: "var(--surface, #fff)",
  color: "var(--ink, #111827)",
  marginTop: 6,
  boxSizing: "border-box",
};
export const labelStyle: React.CSSProperties = { display: "block", fontSize: 12.5, fontWeight: 600, color: "var(--ink-2)", marginTop: 14 };

export function Products() {
  const [items, setItems] = useState<Product[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Product | "new" | null>(null);
  const [showArchived, setShowArchived] = useState(false);

  const refetch = async () => {
    try {
      setItems((await listProducts()).data);
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not load products.");
    }
  };
  useEffect(() => {
    void refetch();
  }, []);

  const shown = items?.filter((p) => showArchived || p.active) ?? null;
  const archived = items?.filter((p) => !p.active).length ?? 0;

  return (
    <div className="scroll">
      <div className="pad fade-in" style={{ maxWidth: 760 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, flexWrap: "wrap", gap: 12 }}>
          <div>
            <h2 style={{ fontSize: 22, fontWeight: 600, margin: 0, letterSpacing: "-0.02em" }}>Products</h2>
            <p style={{ fontSize: 13.5, color: "var(--ink-3)", margin: "4px 0 0" }}>
              What you sell and what it costs. Buyers always pay the price set here, never one
              from a web page.
            </p>
          </div>
          {editing === null && (
            <Btn onClick={() => setEditing("new")}>
              <Ic name="plus" size={14} /> New product
            </Btn>
          )}
        </div>

        {error && <Banner tone="fail">{error}</Banner>}

        {editing !== null && (
          <ProductForm
            product={editing === "new" ? null : editing}
            onClose={() => setEditing(null)}
            onSaved={() => {
              setEditing(null);
              void refetch();
            }}
          />
        )}

        {shown === null ? (
          <div className="card" style={{ padding: 40, textAlign: "center", color: "var(--ink-3)" }}>Loading…</div>
        ) : shown.length === 0 && editing === null ? (
          <div className="card" style={{ padding: 40, textAlign: "center", color: "var(--ink-3)" }}>
            <div style={{ fontSize: 14, marginBottom: 4 }}>No products yet.</div>
            <div style={{ fontSize: 12.5 }}>Add one, then sell it with a payment link or a buy button on your site.</div>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {shown.map((p) => (
              <ProductRow key={p.id} item={p} onEdit={() => setEditing(p)} onChange={refetch} />
            ))}
          </div>
        )}

        {archived > 0 && (
          <button
            type="button"
            className="btn btn-ghost"
            style={{ marginTop: 12 }}
            onClick={() => setShowArchived((v) => !v)}
          >
            {showArchived ? "Hide archived" : `Show archived (${archived})`}
          </button>
        )}
      </div>
    </div>
  );
}

function Thumb({ url, name, size = 52 }: { url: string | null; name: string; size?: number }) {
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: 12,
        background: "var(--surface-2, #f5f6f7)",
        flex: "none",
        overflow: "hidden",
        display: "grid",
        placeItems: "center",
        color: "var(--ink-4, #9aa0a8)",
        fontWeight: 700,
        fontSize: size * 0.38,
      }}
    >
      {url ? <img src={url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : (name.trim()[0] ?? "•").toUpperCase()}
    </div>
  );
}

function ProductRow({ item, onEdit, onChange }: { item: Product; onEdit: () => void; onChange: () => Promise<void> | void }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setErr(null);
    try {
      await fn();
      await onChange();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };
  const copyPrice = async () => {
    if (!item.default_price) return;
    try {
      await navigator.clipboard.writeText(item.default_price.id);
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    } catch {
      /* ignore */
    }
  };
  const sell = () =>
    run(async () => {
      await createPaymentLink({
        line_items: [{ product: item.id, quantity: 1, ...(item.default_price?.unit_amount_usd_cents != null ? { adjustable_quantity: { enabled: true, minimum: 1, maximum: 10 } } : {}) }],
      });
      void navigate({ to: "/links" });
    });

  return (
    <div className="card" style={{ padding: 16, opacity: item.active ? 1 : 0.6 }}>
      <div style={{ display: "flex", gap: 14, alignItems: "center" }}>
        <Thumb url={item.image_url} name={item.name} />
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontWeight: 600, fontSize: 15 }}>
            {item.name}
            {!item.active && <span style={{ fontSize: 12, fontWeight: 600, color: "var(--ink-3)", marginLeft: 8 }}>Archived</span>}
          </div>
          {item.description && (
            <div style={{ fontSize: 12.5, color: "var(--ink-3)", marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {item.description}
            </div>
          )}
        </div>
        <div style={{ fontWeight: 700, fontSize: 15, whiteSpace: "nowrap", textAlign: "right" }}>{priceLabel(item.default_price)}</div>
      </div>
      <div style={{ display: "flex", gap: 8, marginTop: 12, alignItems: "center", flexWrap: "wrap" }}>
        {item.active && (
          <Btn variant="secondary" onClick={() => void sell()} disabled={busy || !item.default_price}>
            <Ic name="link" size={14} /> Sell with a payment link
          </Btn>
        )}
        <Btn variant="ghost" onClick={onEdit} disabled={busy}>Edit</Btn>
        {item.default_price && (
          <Btn variant="ghost" onClick={() => void copyPrice()} title={item.default_price.id}>
            {copied ? "Copied" : "Copy price ID"}
          </Btn>
        )}
        <Btn
          variant="ghost"
          disabled={busy}
          onClick={() => {
            if (item.active && !confirm("Archive this product? Payment links with it stop selling until you restore it.")) return;
            void run(() => updateProduct(item.id, { active: !item.active }));
          }}
        >
          {item.active ? "Archive" : "Restore"}
        </Btn>
      </div>
      {err && (
        <div style={{ marginTop: 10 }}>
          <Banner tone="fail">{err}</Banner>
        </div>
      )}
    </div>
  );
}

type PriceKind = "fixed" | "custom";

function ProductForm({ product, onClose, onSaved }: { product: Product | null; onClose: () => void; onSaved: () => void }) {
  const p = product?.default_price ?? null;
  const c = p?.custom_unit_amount ?? null;
  const dollars = (cents: number | null | undefined) => (cents == null ? "" : (cents / 100).toFixed(2));
  const [name, setName] = useState(product?.name ?? "");
  const [description, setDescription] = useState(product?.description ?? "");
  const [kind, setKind] = useState<PriceKind>(p && p.unit_amount_usd_cents == null ? "custom" : "fixed");
  const [price, setPrice] = useState(dollars(p?.unit_amount_usd_cents));
  const [min, setMin] = useState(c ? dollars(c.minimum_usd_cents) : "5.00");
  const [max, setMax] = useState(c ? dollars(c.maximum_usd_cents) : "500.00");
  const [preset, setPreset] = useState(c ? dollars(c.preset_usd_cents) : "");
  const [file, setFile] = useState<File | null>(null);
  const [removeImage, setRemoveImage] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const priceData = (): PriceData | string => {
    if (kind === "fixed") {
      const cents = toCents(price);
      if (cents == null || cents < 100) return "Set a price of at least $1, like 49.99.";
      return { unit_amount_usd_cents: cents };
    }
    const lo = toCents(min);
    const hi = toCents(max);
    const pre = preset.trim() ? toCents(preset) : null;
    if (lo == null || lo < 100) return "The lowest amount must be at least $1.";
    if (hi == null || hi < lo) return "The highest amount must be at or above the lowest.";
    if (preset.trim() && (pre == null || pre < lo || pre > hi)) return "The suggested amount must sit between the lowest and the highest.";
    return { custom_unit_amount: { minimum_usd_cents: lo, maximum_usd_cents: hi, preset_usd_cents: pre } };
  };

  const samePrice = (d: PriceData): boolean => {
    if (!p) return false;
    if ("unit_amount_usd_cents" in d) return p.unit_amount_usd_cents === d.unit_amount_usd_cents;
    const cc = p.custom_unit_amount;
    return (
      p.unit_amount_usd_cents == null &&
      !!cc &&
      cc.minimum_usd_cents === d.custom_unit_amount.minimum_usd_cents &&
      cc.maximum_usd_cents === d.custom_unit_amount.maximum_usd_cents &&
      (cc.preset_usd_cents ?? null) === (d.custom_unit_amount.preset_usd_cents ?? null)
    );
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    if (!name.trim()) return setErr("Give the product a name.");
    const d = priceData();
    if (typeof d === "string") return setErr(d);
    if (file && file.size > 4 * 1024 * 1024) return setErr("The image must be 4 MB or smaller.");
    setBusy(true);
    try {
      let saved: Product;
      if (product) {
        saved = await updateProduct(product.id, {
          name: name.trim(),
          description: description.trim() || null,
          ...(samePrice(d) ? {} : { default_price_data: d }),
        });
      } else {
        saved = await createProduct({ name: name.trim(), description: description.trim() || null, default_price_data: d });
      }
      if (file) await uploadProductImage(saved.id, file);
      else if (removeImage && product?.image_url) await uploadProductImage(saved.id, null);
      onSaved();
    } catch (e2) {
      setErr(e2 instanceof ApiError || e2 instanceof Error ? e2.message : "Could not save the product.");
      setBusy(false);
    }
  };

  const seg = (k: PriceKind, label: string) => (
    <button
      type="button"
      className={`set-seg-opt${kind === k ? " on" : ""}`}
      aria-pressed={kind === k}
      onClick={() => setKind(k)}
    >
      {label}
    </button>
  );

  return (
    <form className="card" style={{ padding: 20, marginBottom: 14 }} onSubmit={submit}>
      <div style={{ fontWeight: 600, fontSize: 16 }}>{product ? "Edit product" : "New product"}</div>
      <label style={labelStyle}>Name</label>
      <input style={inputStyle} maxLength={250} value={name} onChange={(e) => setName(e.target.value)} placeholder="Blue hoodie" autoFocus />
      <label style={labelStyle}>Description (optional)</label>
      <input style={inputStyle} maxLength={2000} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Shown to the buyer at checkout" />

      <label style={labelStyle}>Price</label>
      <div className="set-seg" style={{ marginTop: 6 }}>
        {seg("fixed", "Fixed price")}
        {seg("custom", "Customer chooses")}
      </div>
      {kind === "fixed" ? (
        <input style={inputStyle} inputMode="decimal" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="49.99" aria-label="Price in USD" />
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8 }}>
          <label style={{ ...labelStyle, marginTop: 8 }}>
            Lowest
            <input style={inputStyle} inputMode="decimal" value={min} onChange={(e) => setMin(e.target.value)} />
          </label>
          <label style={{ ...labelStyle, marginTop: 8 }}>
            Highest
            <input style={inputStyle} inputMode="decimal" value={max} onChange={(e) => setMax(e.target.value)} />
          </label>
          <label style={{ ...labelStyle, marginTop: 8 }}>
            Suggested
            <input style={inputStyle} inputMode="decimal" value={preset} onChange={(e) => setPreset(e.target.value)} placeholder="optional" />
          </label>
        </div>
      )}
      <div style={{ fontSize: 12, color: "var(--ink-3)", marginTop: 6 }}>
        {kind === "custom"
          ? "Good for tips, donations and pay-what-you-want. The buyer types an amount in this range."
          : "In US dollars. Buyers can pay in any coin; you receive the equivalent."}
        {product && " Changing the price keeps your links working at the new price."}
      </div>

      <label style={labelStyle}>Image (optional, PNG, JPEG or WebP, up to 4 MB)</label>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 6 }}>
        {product?.image_url && !removeImage && !file && <Thumb url={product.image_url} name={product.name} size={44} />}
        <input type="file" accept="image/png,image/jpeg,image/webp" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
        {product?.image_url && !file && (
          <label style={{ fontSize: 12.5, color: "var(--ink-2)", display: "flex", gap: 6, alignItems: "center" }}>
            <input type="checkbox" checked={removeImage} onChange={(e) => setRemoveImage(e.target.checked)} /> Remove image
          </label>
        )}
      </div>

      {err && (
        <div style={{ marginTop: 12 }}>
          <Banner tone="fail">{err}</Banner>
        </div>
      )}
      <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
        <Btn type="submit" loading={busy} disabled={busy}>
          {product ? "Save" : "Add product"}
        </Btn>
        <Btn type="button" variant="ghost" onClick={onClose}>
          Cancel
        </Btn>
      </div>
    </form>
  );
}
